import { ArrowLeftOutlined, ArrowRightOutlined } from '@/app/icons';
import { Alert, Button, Card, Flex, Form, Input, Typography, theme } from 'antd';
import { useState } from 'react';
import { authApi } from '@/api/endpoints';
import { errorMessage } from '@/api/client';
import { BrandMark } from '@/components/BrandMark';
import { Caption } from '@/components/Caption';
import { useSessionStore } from '@/stores/session-store';
import { PASSWORD_RULES, USERNAME_RULES } from './validation';

type Mode = 'login' | 'register' | 'registered';

interface LoginValues {
  username: string;
  password: string;
}

interface RegisterValues {
  username: string;
  displayName: string;
  password: string;
  passwordConfirm: string;
  note?: string;
}

export function AuthPage() {
  const { token } = theme.useToken();
  const [mode, setMode] = useState<Mode>('login');

  return (
    <Flex align="center" justify="center" style={{ minHeight: '100dvh', padding: 24 }}>
      <Card style={{ width: 380, maxWidth: '100%' }} styles={{ body: { padding: '32px 28px' } }}>
        <Flex align="center" gap={12} style={{ marginBottom: 20 }}>
          <BrandMark size={48} />
          <div style={{ lineHeight: 1.25 }}>
            <Typography.Text strong style={{ fontSize: token.fontSizeLG }}>
              DỰ ÁN SUN MUSIC
            </Typography.Text>
            <br />
            <Caption>Hệ thống quản lý nội bộ</Caption>
          </div>
        </Flex>
        {mode === 'login' && <LoginForm onRegister={() => setMode('register')} />}
        {mode === 'register' && (
          <RegisterForm onBack={() => setMode('login')} onDone={() => setMode('registered')} />
        )}
        {mode === 'registered' && (
          <Flex vertical gap={16}>
            <Alert
              type="success"
              showIcon
              title="Đã gửi đăng ký"
              description="Giám đốc duyệt xong là bạn đăng nhập được ngay bằng đúng mật khẩu vừa đặt."
            />
            <Button type="primary" block onClick={() => setMode('login')}>
              Quay lại đăng nhập
            </Button>
          </Flex>
        )}
      </Card>
    </Flex>
  );
}

function LoginForm({ onRegister }: { onRegister: () => void }) {
  const login = useSessionStore((state) => state.login);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (values: LoginValues) => {
    setSubmitting(true);
    setError(null);
    try {
      await login({ username: values.username.trim(), password: values.password });
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Form<LoginValues>
      layout="vertical"
      requiredMark={false}
      onFinish={submit}
      disabled={submitting}
    >
      <Form.Item
        name="username"
        label="Tên đăng nhập"
        rules={[{ required: true, message: 'Nhập tên đăng nhập' }]}
      >
        <Input
          autoComplete="username"
          autoCapitalize="none"
          spellCheck={false}
          placeholder="ví dụ: nv.an"
          autoFocus
        />
      </Form.Item>
      <Form.Item
        name="password"
        label="Mật khẩu"
        rules={[{ required: true, message: 'Nhập mật khẩu' }]}
      >
        <Input.Password autoComplete="current-password" />
      </Form.Item>
      {error && <Alert type="error" showIcon title={error} style={{ marginBottom: 16 }} />}
      <Button type="primary" htmlType="submit" block loading={submitting}>
        Đăng nhập
      </Button>
      <Button type="link" block onClick={onRegister} style={{ marginTop: 8 }}>
        Chưa có tài khoản? Đăng ký <ArrowRightOutlined />
      </Button>
    </Form>
  );
}

function RegisterForm({ onBack, onDone }: { onBack: () => void; onDone: () => void }) {
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (values: RegisterValues) => {
    setSubmitting(true);
    setError(null);
    try {
      await authApi.register({
        username: values.username.trim().toLowerCase(),
        displayName: values.displayName.trim(),
        password: values.password,
        note: values.note?.trim() ?? '',
      });
      onDone();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Form<RegisterValues>
      layout="vertical"
      requiredMark={false}
      onFinish={submit}
      disabled={submitting}
    >
      <Typography.Paragraph type="secondary">
        Giám đốc duyệt và gán chức vụ; duyệt xong bạn đăng nhập bằng đúng mật khẩu đặt ở đây.
      </Typography.Paragraph>
      <Form.Item name="username" label="Tên đăng nhập" rules={USERNAME_RULES}>
        <Input
          autoCapitalize="none"
          spellCheck={false}
          placeholder="ví dụ: nv.cuong — chữ thường, số, dấu . _ -"
        />
      </Form.Item>
      <Form.Item
        name="displayName"
        label="Họ và tên"
        rules={[{ required: true, min: 2, max: 60, message: 'Họ tên phải từ 2 đến 60 ký tự' }]}
      >
        <Input placeholder="ví dụ: Trần Văn Cường" />
      </Form.Item>
      <Form.Item name="password" label="Mật khẩu" rules={PASSWORD_RULES}>
        <Input.Password autoComplete="new-password" placeholder="ít nhất 8 ký tự" />
      </Form.Item>
      <Form.Item
        name="passwordConfirm"
        label="Gõ lại mật khẩu"
        dependencies={['password']}
        rules={[
          { required: true, message: 'Gõ lại mật khẩu' },
          ({ getFieldValue }) => ({
            validator: (_, value) =>
              !value || getFieldValue('password') === value
                ? Promise.resolve()
                : Promise.reject(new Error('Hai lần gõ mật khẩu không khớp')),
          }),
        ]}
      >
        <Input.Password autoComplete="new-password" />
      </Form.Item>
      <Form.Item name="note" label="Bộ phận mong muốn · số điện thoại (không bắt buộc)">
        <Input maxLength={200} placeholder="ví dụ: Phòng Nội dung, nhóm SEO — 09xx…" />
      </Form.Item>
      {error && <Alert type="error" showIcon title={error} style={{ marginBottom: 16 }} />}
      <Button type="primary" htmlType="submit" block loading={submitting}>
        Gửi đăng ký
      </Button>
      <Button type="link" block onClick={onBack} style={{ marginTop: 8 }}>
        <ArrowLeftOutlined /> Quay lại đăng nhập
      </Button>
    </Form>
  );
}
